import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { Shipment } from '../models/Shipments';
import { useShipments } from './useShipments';
import { getInvoicesOrdersFilterOptions, LoadColumnValuesCallback } from '../config/filterOptions';
import { FiltersApiAdapter } from '../adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '../adapters/TeamsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCountries } from './useCountries';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  order_id: 'order_id',
  ref: 'ref',
  order_created_at: 'order_created_at',
  processed_at: 'processed_at',
  date_tracking_update: 'date_tracking_update',
  last_status_at: 'last_status_at',
  shipping_date: 'shipping_date',
  financial_status: 'financial_status',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

// 🔍 La búsqueda ahora usa un parámetro simple ?search=valor
// El backend se encarga de buscar en el campo 'ref' con operador 'contains'
// No es necesario construir un advanced_filter para la búsqueda

/**
 * useShipmentsMain - Hook principal para gestionar toda la vista de Invoices/Orders
 *
 * Orquesta todos los aspectos de la gestión de shipments elegibles para facturación:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas (id, ref, order_created_at, processed_at, date_tracking_update, last_status_at)
 * - Selección múltiple de shipments
 * - Carga dinámica de valores para filtros (shops, payment methods, couriers, teams, countries)
 * - Sincronización bidireccional con query params de la URL
 * - Detección de cambios en filtros para mostrar botón de prefacturación
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */
export const useShipmentsMain = () => {
  const { t } = useTranslation('invoicesOrdersModule');

  // 🎯 Cargar países para el filtro
  const { countries } = useCountries();

  // 🎯 Función para cargar opciones dinámicamente
  const loadColumnValues: LoadColumnValuesCallback = useCallback(async (columnValue: string) => {
    try {
      const httpClient = createHttpClient(apiClientWH);
      const filtersAdapter = new FiltersApiAdapter(httpClient);

      switch (columnValue) {
        case 'shop_id': {
          const shops = await filtersAdapter.getShops();
          return shops.map((shop) => ({
            label: shop.name,
            value: String(shop.id),
          }));
        }
        case 'payment_method_id': {
          const paymentMethods = await filtersAdapter.getPaymentMethods();
          return paymentMethods.map((method) => ({
            label: method.name,
            value: String(method.id),
          }));
        }
        case 'courier_id': {
          const couriers = await filtersAdapter.getCouriers();
          return couriers.map((courier) => ({
            label: courier.name,
            value: String(courier.id),
          }));
        }
        case 'team_id': {
          const teamsAdapter = new TeamsApiAdapter(httpClient);
          const teams = await teamsAdapter.getTeamsChild();
          return teams.map((team) => ({
            label: team.name,
            value: team.id.toString(),
          }));
        }
        case 'status_ids': {
          // Sin API: mismos estados que Orders (orderStatusConfig)
          const { orderStatusFilterOptions } = await import('../config/orderStatusConfig');
          return orderStatusFilterOptions.map((opt) => ({
            label: t(opt.labelKey),
            value: String(opt.status_id),
          }));
        }
        case 'shipment_status_ids': {
          const shipmentStatuses = await filtersAdapter.getShipmentStatuses();
          return shipmentStatuses.map((s) => ({
            label: s.name,
            value: String(s.id),
          }));
        }
        default:
          return [];
      }
    } catch {
      return [];
    }
  }, []);

  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: '' as string },
    sortDirection: { type: 'string', default: '' as string },
    financialStatus: { type: 'string', default: '' as string },
  });

  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(() =>
    parseFiltersFromParams(queryParams.filters),
  );
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(filterConditions));
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      return null;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';
    return {
      field: sortField,
      direction,
    };
  });
  const [financialStatus, setFinancialStatusState] = useState<string | undefined>(() => {
    return queryParams.financialStatus || undefined;
  });

  // Estado de selecciÃ³n
  const [selectedShipments, setSelectedShipments] = useState<Set<number>>(new Set());
  const [selectedShipmentsDataMap, setSelectedShipmentsDataMap] = useState<Map<number, Shipment>>(new Map());

  // SincronizaciÃ³n con query params
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      setSorting((prev) => (prev === null ? prev : null));
      return;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  useEffect(() => {
    const status = queryParams.financialStatus || undefined;
    setFinancialStatusState((prev) => (prev === status ? prev : status));
  }, [queryParams.financialStatus]);

  // Estados de columnas visibles - Solo las 12 columnas de la vista de Invoices
  const tableColumns = [
    { accessorKey: 'order', header: t('table.order') }, // Pedido
    { accessorKey: 'order_id', header: t('table.order_id') }, // ID Pedido
    { accessorKey: 'customer', header: t('table.customer') }, // Cliente
    { accessorKey: 'shop', header: t('table.shop') }, // Tienda
    { accessorKey: 'shipping_date', header: t('table.shipment_date') }, // Fecha
    { accessorKey: 'internal_status', header: t('table.status') }, // Estado Interno
    { accessorKey: 'logistic_status', header: t('table.logistic_status') }, // Estado LogÃ­stico
    { accessorKey: 'date_tracking_update', header: t('table.last_logistic_update') }, // Última act. logística
    { accessorKey: 'last_status_at', header: t('table.last_status_at') },
    { accessorKey: 'courier', header: t('table.courier') }, // Courier
    { accessorKey: 'reference', header: t('table.reference') }, // Referencia
    { accessorKey: 'order_price', header: t('table.total') }, // Precio Pedido
    { accessorKey: 'financial_status', header: t('table.financial_status') }, // Estado Financiero
    { accessorKey: 'pre_invoice_id', header: t('table.pre_invoice_no') }, // NÂ° Pre-Factura
    { accessorKey: 'invoice_id', header: t('table.invoice_no') }, // NÂ° Factura
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.accessorKey));

  // 🔍 Búsqueda simple - se pasa directamente el término de búsqueda
  // El backend busca automáticamente en el campo 'ref' con operador 'contains'

  // Columnas que son parámetros de URL (no advanced_filters)
  const URL_PARAM_COLUMNS = new Set([
    'courier_id',
    'team_id',
    'shop_id',
    'payment_method_id',
    'period',
    'status_ids',
    'shipment_status_ids',
    'pre_invoice_id',
    'invoice_id',
  ]);

  const columnToApiParam: Record<string, string> = {
    courier_id: 'courier_ids',
    team_id: 'team_ids',
    shop_id: 'shop_ids',
    payment_method_id: 'payment_method_ids',
    period: 'period',
    status_ids: 'status_ids',
    shipment_status_ids: 'shipment_status_ids',
    pre_invoice_id: 'pre_invoice_ids',
    invoice_id: 'invoice_ids',
  };

  const toNumberArray = (val: unknown): number[] => {
    if (val == null) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr.map((v) => (typeof v === 'number' ? v : parseInt(String(v), 10))).filter((n) => !Number.isNaN(n));
  };

  // Procesar filtros
  const processedFilters = useMemo(() => {
    try {
      // Excluir columnas que son parámetros de URL (van en query string, no en advanced_filters)
      const conditionsForAdvancedFilter = filterConditions.filter((c) => !URL_PARAM_COLUMNS.has(c.column));

      const searchFilterFromConditions =
        conditionsForAdvancedFilter.length > 0
          ? {
              filters: conditionsForAdvancedFilter.map((condition) => {
                const requiresArray = ['in', 'not_in'].includes(condition.action);
                const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

                const value = isNullOperator
                  ? undefined
                  : requiresArray
                    ? Array.isArray(condition.value)
                      ? condition.value
                      : [condition.value]
                    : condition.value;

                return {
                  column: condition.column,
                  operator: condition.action,
                  ...(value !== undefined && { value: value }),
                };
              }),
              logic: 'AND',
            }
          : undefined;

      // Extraer filtros directos (courier_ids, team_ids, etc.) desde filterConditions
      const directFilters: Record<string, number[] | string> = {};
      for (const c of filterConditions) {
        const paramKey = columnToApiParam[c.column];
        if (!paramKey || ['is_null', 'is_not_null'].includes(c.action)) continue;
        if (paramKey === 'period') {
          const v = c.value;
          directFilters.period = Array.isArray(v) ? ((v[0] as string) ?? '') : String(v ?? '');
        } else {
          const nums = toNumberArray(c.value);
          if (nums.length > 0) {
            directFilters[paramKey] = nums;
          }
        }
      }

      // Mapear el campo de sorting a los parámetros de la API
      const apiField = sorting?.field ? fieldToApiMapping[sorting.field] || 'id' : 'id';
      const apiDirection = sorting?.direction || 'desc';

      return {
        sortBy: apiField,
        sortDirection: apiDirection,
        ...(searchFilterFromConditions && {
          searchFilter: searchFilterFromConditions,
        }),
        ...directFilters,
      };
    } catch {
      return {};
    }
  }, [filterConditions, sorting]);

  // ðŸŽ¯ Memoizar el objeto de filtros para evitar re-renders infinitos
  const queryFilters = useMemo(
    () => ({
      search: searchTerm, // 🔍 Parámetro simple de búsqueda
      ...processedFilters,
      financial_status: financialStatus,
      sorting,
    }),
    [searchTerm, processedFilters, financialStatus, sorting],
  );

  // Hooks de datos
  const { shipments, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, total } =
    useShipments(queryFilters);

  // Detectar si estamos ordenando (isLoading + sorting activo)
  const isSortingActive = useMemo(() => {
    return isFetching && sorting !== null;
  }, [isFetching, sorting]);

  // Usar los datos del Map para los contadores:

  // Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setSearchTerm((prev) => (prev === trimmedValue ? prev : trimmedValue));
      updateParam('search', trimmedValue);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);

  const handleFinancialStatusChange = useCallback(
    (value?: string) => {
      setFinancialStatusState((prev) => (prev === value ? prev : value));

      const statusString = value || '';
      if ((queryParams.financialStatus ?? '') !== statusString) {
        updateParam('financialStatus', statusString);
      }
    },
    [queryParams.financialStatus, updateParam],
  );

  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);

      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  // Handlers de selecciÃ³n
  const handleToggleShipmentSelection = useCallback((shipment: Shipment) => {
    setSelectedShipments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(shipment.id)) {
        newSet.delete(shipment.id);
        setSelectedShipmentsDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.delete(shipment.id);
          return newMap;
        });
      } else {
        newSet.add(shipment.id);
        setSelectedShipmentsDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(shipment.id, shipment);
          return newMap;
        });
      }

      return newSet;
    });
  }, []);

  const handleSelectAllShipments = useCallback(() => {
    setSelectedShipments((prev) => {
      if (shipments.every((shipment) => prev.has(shipment.id))) {
        setSelectedShipmentsDataMap(new Map());
        return new Set();
      } else {
        setSelectedShipmentsDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          shipments.forEach((shipment) => {
            newMap.set(shipment.id, shipment);
          });
          return newMap;
        });
        return new Set(shipments.map((shipment) => shipment.id));
      }
    });
  }, [shipments]);

  const handleClearSelection = useCallback(() => {
    setSelectedShipments(new Set());
    setSelectedShipmentsDataMap(new Map());
  }, []);

  // ðŸŽ¯ Handler para cambios de selecciÃ³n desde AG Grid
  const handleSelectionChanged = useCallback(
    (selectedIds: Set<number>) => {
      setSelectedShipments(selectedIds);

      // Actualizar el mapa de datos seleccionados
      setSelectedShipmentsDataMap(() => {
        const newMap = new Map();
        selectedIds.forEach((id) => {
          const shipment = shipments.find((s) => s.id === id);
          if (shipment) {
            newMap.set(id, shipment);
          }
        });
        return newMap;
      });
    },
    [shipments],
  );

  // Props para componentes
  const shipmentsViewProps = useMemo(
    () => ({
      shipments,
      isLoading,
      isFetching,
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns: [...selectedColumns, 'actions'],
      refetch,
      selectedShipments,
      onToggleShipmentSelection: handleToggleShipmentSelection,
      onSelectAllShipments: handleSelectAllShipments,
      onClearSelection: handleClearSelection,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [
      shipments,
      isLoading,
      isFetching,
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns,
      refetch,
      selectedShipments,
      handleToggleShipmentSelection,
      handleSelectAllShipments,
      handleClearSelection,
      sorting,
      handleSortingChange,
    ],
  );

  // Detectar si hay filtros avanzados activos (para habilitar botón Prefacturar)
  // 🎯 NOTA: El botón Prefacturar solo se activa con filtros avanzados, NO con búsqueda simple
  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0;
  }, [filterConditions.length]);

  // Filtros para exportación (mismo formato que GET eligible)
  const exportFilters = useMemo(
    () => ({
      search: searchTerm,
      financial_status: financialStatus,
      ...processedFilters,
    }),
    [searchTerm, financialStatus, processedFilters],
  );

  return {
    // Estados
    filterConditions,
    financialStatus,
    selectedShipments,
    selectedColumns,
    tableColumns,
    sorting,
    searchTerm,

    // Filtros para export
    exportFilters,

    // Datos
    shipments,
    isLoading,
    isFetching,
    isSortingActive,
    error,
    total,

    // Props para componentes
    shipmentsViewProps,

    // Handlers
    setFinancialStatus: handleFinancialStatusChange,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selecciÃ³n
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    search: searchTerm, // 🔍 Parámetro simple de búsqueda
    filterOptions: getInvoicesOrdersFilterOptions(t, loadColumnValues, false, countries),
  };
};
